import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FULFILMENT = ["NOT_READY", "READY_FOR_AJDN", "SENT_TO_AJDN", "RECEIVED_BY_AJDN", "PROCESSING", "PRODUCTION", "PACKING", "SHIPPED", "DELIVERED", "FAILED", "CANCELLED", "RETURNED"];
const PAYMENT_LABELS = { pending: "IN AFWACHTING", paid: "BETAALD", failed: "MISLUKT", cancelled: "GEANNULEERD", refunded: "TERUGBETAALD" };
const FULFILMENT_LABELS = { NOT_READY: "NIET GEREED", READY_FOR_AJDN: "KLAAR VOOR AJDN", SENT_TO_AJDN: "VERZONDEN NAAR AJDN", RECEIVED_BY_AJDN: "ONTVANGEN DOOR AJDN", PROCESSING: "VERWERKEN", PRODUCTION: "PRODUCTIE", PACKING: "INPAKKEN", SHIPPED: "VERZONDEN", DELIVERED: "GELEVERD", FAILED: "MISLUKT", CANCELLED: "GEANNULEERD", RETURNED: "GERETOURNEERD" };
const pl = (s) => PAYMENT_LABELS[s] || (s || "").toUpperCase();
const fl = (s) => FULFILMENT_LABELS[s] || s;
const ORDER_LABELS = { open: "OPEN", pending: "IN AFWACHTING", confirmed: "BEVESTIGD", processing: "VERWERKEN", cancelled: "GEANNULEERD", completed: "VOLTOOID", fulfilled: "AFGEHANDELD", refunded: "TERUGBETAALD", returned: "GERETOURNEERD" };
const AJDN_LABELS = { NOT_SENT: "NIET VERZONDEN", SYNC_FAILED: "SYNC MISLUKT", SENT: "VERZONDEN", RECEIVED: "ONTVANGEN", CANCELLED: "GEANNULEERD" };
const ol = (s) => ORDER_LABELS[(s || "").toLowerCase()] || (s || "").toUpperCase();
const al = (s) => AJDN_LABELS[s] || FULFILMENT_LABELS[s] || s;

function Badge({ children, tone = "neutral" }) {
  const map = { paid: "bg-[#1D5B2A] text-white", pending: "bg-[#8A6D1F] text-white", failed: "bg-[#9B1C1C] text-white", neutral: "border border-[#E8E1D8]" };
  return <span className={`label-x px-2 py-0.5 ${map[tone] || map.neutral}`}>{children}</span>;
}

function OrderDetail({ number, onClose, onChange }) {
  const [o, setO] = useState(null);
  const [note, setNote] = useState("");
  const load = () => api.get(`/admin/orders/${number}`).then(({ data }) => setO(data)).catch(() => {});
  useEffect(() => { load(); }, [number]);
  if (!o) return <p className="label-x p-8">LADEN…</p>;

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); onChange?.(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const ajdn = o.ajdn || {};

  const timeline = [
    ["Bestelling aangemaakt", o.created_at],
    ...(o.paid_at ? [["Betaling bevestigd", o.paid_at]] : [["Betaling in behandeling", null]]),
    ...(o.paid_at ? [["Klaar voor fulfilment", o.paid_at]] : []),
    ...(ajdn.sent_at ? [["Verzonden naar AJDN", ajdn.sent_at]] : []),
    ...(o.shipped_at ? [["Verzonden", o.shipped_at]] : []),
    ...(o.delivered_at ? [["Geleverd", o.delivered_at]] : []),
  ];

  return (
    <div className="space-y-5" data-testid={`order-detail-${number}`}>
      <div className="flex flex-wrap gap-2">
        <Badge tone={o.payment_status === "paid" ? "paid" : o.payment_status === "pending" ? "pending" : "failed"}>BETALING: {pl(o.payment_status)}</Badge>
        <Badge>BESTELLING: {ol(o.order_status)}</Badge>
        <Badge>FULFILMENT: {fl(o.fulfilment_status)}</Badge>
        <Badge>AJDN: {al(ajdn.status || "NOT_SENT")}</Badge>
      </div>

      {o.payment_status !== "paid" && (
        <button onClick={() => act(() => api.post(`/admin/orders/${number}/test-pay`), "Gemarkeerd als betaald (TEST)")} className="btn-x-outline text-[0.65rem]" data-testid="test-pay-btn">MARKEER ALS BETAALD (TESTMODUS)</button>
      )}

      <div className="border border-[#E8E1D8] bg-white p-4">
        <p className="label-x mb-2">AJDN FULFILMENT</p>
        <div className="text-xs text-[#77736D] space-y-1">
          <p>AJDN Order ID: {ajdn.ajdn_order_id || "—"}</p>
          <p>Laatste sync: {ajdn.last_sync ? new Date(ajdn.last_sync).toLocaleString("nl-NL") : "—"}</p>
          <p>Aantal pogingen: {ajdn.retry_count || 0}</p>
          {ajdn.last_error && <p className="text-[#9B1C1C]">Fout: {ajdn.last_error}</p>}
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button disabled={o.payment_status !== "paid"} onClick={() => act(() => api.post(`/admin/orders/${number}/ajdn/send`), "Verzonden naar AJDN")} className="btn-x text-[0.6rem] disabled:opacity-40" data-testid="ajdn-send">VERZEND NAAR AJDN</button>
          <button onClick={() => act(() => api.post(`/admin/orders/${number}/ajdn/retry`), "Opnieuw geprobeerd")} className="btn-x-outline text-[0.6rem]" data-testid="ajdn-retry">OPNIEUW</button>
          <button onClick={() => act(() => api.post(`/admin/orders/${number}/ajdn/refresh`), "Vernieuwd")} className="btn-x-outline text-[0.6rem]" data-testid="ajdn-refresh">VERNIEUW STATUS</button>
          <button onClick={() => act(() => api.post(`/admin/orders/${number}/ajdn/cancel`), "Geannuleerd")} className="btn-x-outline text-[0.6rem]" data-testid="ajdn-cancel">ANNULEER AJDN</button>
        </div>
        <p className="label-x mt-3">Bestellingen gaan alleen naar AJDN wanneer ze BETAALD zijn. Idempotency-sleutel ONUSA-{number} voorkomt duplicaten.</p>
      </div>

      <div className="border border-[#E8E1D8] bg-white p-4">
        <p className="label-x mb-2">HANDMATIGE STATUS</p>
        <select value={o.fulfilment_status} onChange={(e) => act(() => api.patch(`/admin/orders/${number}/status`, { fulfilment_status: e.target.value }), "Status bijgewerkt")} className="border border-[#E8E1D8] bg-white px-2 py-1 text-xs font-mono" data-testid="manual-fulfilment">
          {FULFILMENT.map((s) => <option key={s} value={s}>{fl(s)}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4 text-xs">
        <div className="border border-[#E8E1D8] bg-white p-4"><p className="label-x mb-2">KLANT</p><p>{o.email}</p><p>{o.phone}</p><p className="mt-2">{o.shipping_address?.first_name} {o.shipping_address?.last_name}</p><p className="text-[#77736D]">{o.shipping_address?.street} {o.shipping_address?.house_number}, {o.shipping_address?.postal_code} {o.shipping_address?.city}</p></div>
        <div className="border border-[#E8E1D8] bg-white p-4"><p className="label-x mb-2">TOTALEN</p><p>Subtotaal €{o.subtotal?.toFixed(2)}</p><p>Korting €{o.discount_amount?.toFixed(2)}</p><p>Verzending €{o.shipping?.toFixed(2)}</p><p>BTW €{o.vat?.toFixed(2)}</p><p className="font-medium">Totaal €{o.total?.toFixed(2)}</p></div>
      </div>

      <div className="border border-[#E8E1D8] bg-white p-4">
        <p className="label-x mb-2">ARTIKELEN</p>
        {o.items.map((it, i) => <div key={i} className="flex justify-between text-xs py-1"><span>{it.name} {it.size ? `(${it.size})` : ""} · SKU {it.sku} ×{it.quantity}</span><span className="font-mono">€{it.line_total?.toFixed(2)}</span></div>)}
      </div>

      {o.tracking?.number && <div className="border border-[#E8E1D8] bg-white p-4 text-xs"><p className="label-x mb-2">TRACK & TRACE</p><p>{o.tracking.carrier} — {o.tracking.number}</p></div>}

      <div className="border border-[#E8E1D8] bg-white p-4">
        <p className="label-x mb-3">TIJDLIJN</p>
        <ol className="space-y-2" data-testid="order-timeline">
          {timeline.map(([label, ts], i) => (
            <li key={i} className="flex items-center gap-3 text-xs">
              <span className={`w-2 h-2 rounded-full ${ts ? "bg-[#1D5B2A]" : "bg-[#E8E1D8]"}`} />
              <span className="flex-1">{label}</span>
              <span className="text-[#77736D] font-mono">{ts ? new Date(ts).toLocaleString("nl-NL") : "—"}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="border border-[#E8E1D8] bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="label-x">TRANSACTIONELE E-MAIL</p>
          <div className="flex gap-2">
            <button onClick={() => act(() => api.post(`/admin/orders/${number}/resend-email`, { type: "confirmation" }), "Verstuurd")} className="btn-x-outline text-[0.6rem]" data-testid="resend-confirmation">RESEND BEVESTIGING</button>
            <button onClick={() => act(() => api.post(`/admin/orders/${number}/resend-email`, { type: "shipping" }), "Verstuurd")} className="btn-x-outline text-[0.6rem]" data-testid="resend-shipping">RESEND VERZENDING</button>
          </div>
        </div>
        <p className="label-x text-[#77736D]">Via Resend. Levering afhankelijk van geverifieerd verzenddomein.</p>
      </div>

      <div className="border border-[#E8E1D8] bg-white p-4">
        <p className="label-x mb-2">INTERNE NOTITIES</p>
        {(o.notes || []).map((n) => <p key={n.id} className="text-xs text-[#77736D] mb-1">{n.body} <span className="opacity-60">· {n.author} · {new Date(n.created_at).toLocaleDateString("nl-NL")}</span></p>)}
        <div className="flex gap-2 mt-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Interne notitie…" className="flex-1 border border-[#E8E1D8] px-2 py-1.5 text-xs" data-testid="order-note-input" />
          <button onClick={() => { if (note.trim()) act(() => api.post(`/admin/orders/${number}/notes`, { body: note }).then(() => setNote("")), "Notitie toegevoegd"); }} className="label-x" data-testid="order-note-add">TOEVOEGEN</button>
        </div>
      </div>

      <div className="border border-[#E8E1D8] bg-white p-4">
        <p className="label-x mb-2">INTEGRATIELOG</p>
        {(o.integration_logs || []).length === 0 ? <p className="text-xs text-[#77736D]">Geen gebeurtenissen.</p> :
          o.integration_logs.map((l) => <p key={l.id} className="text-xs text-[#77736D]">{new Date(l.created_at).toLocaleString("nl-NL")} · {l.integration} · {l.event} · {l.response_status} {l.error && `· ${l.error}`}</p>)}
      </div>
      <button onClick={onClose} className="btn-x-outline">SLUITEN</button>
    </div>
  );
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState({ q: "", payment_status: "", fulfilment_status: "" });
  const [open, setOpen] = useState(null);
  const load = () => {
    const p = new URLSearchParams(Object.entries(filter).filter(([, v]) => v));
    api.get(`/admin/orders?${p}`).then(({ data }) => setOrders(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between mb-6">
        <h1 className="heading-x text-2xl">BESTELLINGEN & AJDN</h1>
        <a href={`${api.defaults.baseURL}/admin/export/orders.csv`} className="btn-x-outline text-[0.65rem]" data-testid="export-orders">EXPORT CSV</a>
      </div>
      <div className="flex flex-wrap gap-3 mb-4">
        <input placeholder="Zoek # of e-mail" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} className="border border-[#E8E1D8] bg-white px-3 py-2 text-sm" data-testid="order-search" />
        <select value={filter.payment_status} onChange={(e) => setFilter({ ...filter, payment_status: e.target.value })} className="border border-[#E8E1D8] bg-white px-3 py-2 text-xs font-mono">
          <option value="">ALLE BETALINGEN</option>{["pending", "paid", "failed", "cancelled", "refunded"].map((s) => <option key={s} value={s}>{pl(s)}</option>)}
        </select>
      </div>
      <div className="border border-[#E8E1D8] bg-white divide-y divide-[#E8E1D8]">
        {orders.map((o) => (
          <button key={o.number} onClick={() => setOpen(o.number)} className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 p-3 text-left hover:bg-[#E8E1D8]/30" data-testid={`admin-order-row-${o.number}`}>
            <span className="font-mono text-xs w-24 shrink-0">{o.number}</span>
            <span className="text-xs flex-1 min-w-[8rem] truncate">{o.email}</span>
            <Badge tone={o.payment_status === "paid" ? "paid" : o.payment_status === "pending" ? "pending" : "failed"}>{pl(o.payment_status)}</Badge>
            <Badge>{fl(o.fulfilment_status)}</Badge>
            <span className="font-mono text-xs w-20 text-right shrink-0">€{o.total?.toFixed(2)}</span>
          </button>
        ))}
        {orders.length === 0 && <p className="label-x p-8 text-center">GEEN BESTELLINGEN</p>}
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-2xl bg-[#F7F5F1] max-h-[92vh] overflow-y-auto no-scrollbar">
          <DialogHeader><DialogTitle className="heading-x text-sm">BESTELLING {open}</DialogTitle></DialogHeader>
          {open && <OrderDetail number={open} onClose={() => setOpen(null)} onChange={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
